import { useEffect, useState } from 'react';
import {
  ShieldAlert, Save, CheckCircle2, AlertCircle, Info, Building2,
} from 'lucide-react';
import PageHeader from '../../../components/ui/PageHeader';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import Badge from '../../../components/ui/Badge';
import QboPicker, { accountToOption, classToOption } from '../../../components/mec/QboPicker';
import {
  listRouting,
  getAccountsForRealm,
  getClassesForRealm,
  updateRouting,
} from '../../../api/mec';
import { formatRelative } from '../../../utils/formatters';

// Per MEC_REQUIREMENTS.md §5: "Parts Purchases uses CostOfGoodsSold, Expense, OtherExpense.
// Sublet same. Use 'All' for v1 simplicity." We send 'All' and let the bookkeeper
// pick — keeps the dropdown forgiving when QBO uses non-standard types.
const ACCOUNT_TYPE_FILTER = 'All';

export default function AccountRouting() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    listRouting()
      .then((data) => {
        if (!alive) return;
        setRows((data?.shops || []).map((r) => ({ ...r, _dirty: false, _saving: false, _saveError: null, _savedAt: null })));
      })
      .catch((err) => {
        if (!alive) return;
        setError(err?.message || 'Failed to load routing rows');
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  // Mutate one row in place by shop_id.
  const updateRow = (shopId, patch) => {
    setRows((prev) =>
      prev.map((r) => (r.shop_id === shopId ? { ...r, ...patch } : r))
    );
  };

  const onPickAccount = (shopId, field, idField, nameField, picked) => {
    updateRow(shopId, {
      [idField]: picked.id,
      [nameField]: picked.name,
      _dirty: true,
      _saveError: null,
      _savedAt: null,
    });
  };

  const onClearField = (shopId, idField, nameField) => {
    updateRow(shopId, {
      [idField]: '',
      [nameField]: '',
      _dirty: true,
      _saveError: null,
      _savedAt: null,
    });
  };

  const saveRow = async (row) => {
    updateRow(row.shop_id, { _saving: true, _saveError: null });
    try {
      const updated = await updateRouting(row.shop_id, {
        parts_purchases_account_id: row.parts_purchases_account_id || '',
        parts_purchases_account_name: row.parts_purchases_account_name || '',
        sublet_account_id: row.sublet_account_id || '',
        sublet_account_name: row.sublet_account_name || '',
        qbo_class_id: row.qbo_class_id || '',
        qbo_class_name: row.qbo_class_name || '',
      });
      updateRow(row.shop_id, {
        ...updated,
        _dirty: false,
        _saving: false,
        _saveError: null,
        _savedAt: new Date().toISOString(),
      });
    } catch (err) {
      updateRow(row.shop_id, {
        _saving: false,
        _saveError: err?.message || 'Save failed',
      });
    }
  };

  return (
    <div>
      <PageHeader
        title="Account Routing"
        description="Tell the Month-End Close module which QuickBooks accounts to READ for each shop. Selections are saved to our database only — nothing in QuickBooks is modified."
        breadcrumbs={[
          { label: 'Month-End Close', href: '/mec' },
          { label: 'Settings' },
          { label: 'Account Routing' },
        ]}
      />

      {/* Read-only QBO banner */}
      <div className="mb-5 flex items-start gap-3 p-4 rounded-[var(--radius-md)] bg-[var(--color-action-50)] border border-[var(--color-action-100)]">
        <ShieldAlert size={16} className="shrink-0 mt-0.5 text-[var(--color-action-600)]" />
        <div className="text-[13px] text-[var(--color-action-700)] leading-relaxed">
          <strong className="font-semibold">Read-only against QuickBooks.</strong>{' '}
          Selections here only tell the module which accounts to <em>read</em> —
          nothing in QuickBooks will ever be modified.
        </div>
      </div>

      {/* Loading + error states */}
      {loading && (
        <Card>
          <div className="flex items-center gap-3 py-8 justify-center text-[var(--color-warm-500)]">
            <span className="inline-block w-4 h-4 border-2 border-[var(--color-warm-300)] border-t-[var(--color-warm-700)] rounded-full animate-spin" />
            <span className="text-[13px]">Loading shop routing…</span>
          </div>
        </Card>
      )}

      {!loading && error && (
        <Card>
          <div className="flex items-start gap-3 py-3">
            <AlertCircle size={16} className="shrink-0 mt-0.5 text-[var(--color-danger-600)]" />
            <div>
              <div className="text-[14px] font-semibold text-[var(--color-warm-900)]">
                Could not load routing.
              </div>
              <div className="text-[12px] text-[var(--color-warm-600)] mt-1">{error}</div>
              <div className="text-[12px] text-[var(--color-warm-500)] mt-2">
                The Month-End Close backend may not be deployed yet, or your session expired.
              </div>
            </div>
          </div>
        </Card>
      )}

      {!loading && !error && rows.length === 0 && (
        <Card>
          <div className="text-center py-10">
            <Building2 size={32} className="mx-auto mb-3 text-[var(--color-warm-400)]" />
            <div className="text-[14px] font-semibold text-[var(--color-warm-900)]">
              No shops on file
            </div>
            <div className="text-[12px] text-[var(--color-warm-500)] mt-1">
              The Month-End Close database doesn't have any shops seeded. Reach out to Danish.
            </div>
          </div>
        </Card>
      )}

      {!loading && !error && rows.length > 0 && (
        <Card padded={false}>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-[var(--color-warm-100)] bg-[var(--color-cream-100)]">
                  <Th>Shop</Th>
                  <Th>QBO Realm</Th>
                  <Th>Parts Purchases Account</Th>
                  <Th>Sublet Account</Th>
                  <Th>QBO Class</Th>
                  <Th align="right">Save</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <RoutingRow
                    key={row.shop_id}
                    row={row}
                    onPickAccount={onPickAccount}
                    onClearField={onClearField}
                    onSave={() => saveRow(row)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="mt-4 flex items-start gap-2 text-[12px] text-[var(--color-warm-500)]">
        <Info size={12} className="shrink-0 mt-0.5" />
        <span>
          Account dropdowns fetch live from QuickBooks each time you open them.
          Use the refresh icon inside the dropdown if you just added a new account in QBO.
        </span>
      </div>
    </div>
  );
}

function Th({ children, align = 'left' }) {
  return (
    <th
      className={`
        px-4 py-2.5 text-[10px] font-bold uppercase tracking-[1.2px]
        text-[var(--color-warm-500)]
        ${align === 'right' ? 'text-right' : 'text-left'}
      `}
    >
      {children}
    </th>
  );
}

function RoutingRow({ row, onPickAccount, onClearField, onSave }) {
  const hasRealm = Boolean(row.realm_id);
  const partsValue = row.parts_purchases_account_id
    ? { id: row.parts_purchases_account_id, name: row.parts_purchases_account_name }
    : null;
  const subletValue = row.sublet_account_id
    ? { id: row.sublet_account_id, name: row.sublet_account_name }
    : null;
  const classValue = row.qbo_class_id
    ? { id: row.qbo_class_id, name: row.qbo_class_name }
    : null;

  // Each picker re-fetches on every open — closure captures realm_id only.
  const fetchAccounts = () =>
    getAccountsForRealm(row.realm_id, ACCOUNT_TYPE_FILTER).then((data) => ({
      options: (data?.accounts || []).filter((a) => a.active).map(accountToOption),
    }));

  const fetchClasses = () =>
    getClassesForRealm(row.realm_id).then((data) => ({
      options: (data?.classes || []).filter((c) => c.active).map(classToOption),
    }));

  const realmLabel = hasRealm
    ? (row.realm_id.length > 8 ? `…${row.realm_id.slice(-6)}` : row.realm_id)
    : '—';

  return (
    <tr className="border-b border-[var(--color-warm-100)] last:border-b-0 hover:bg-[var(--color-cream-100)] transition-colors">
      {/* Shop */}
      <td className="px-4 py-3 align-middle">
        <div className="text-[14px] font-semibold text-[var(--color-warm-900)]">
          {row.shop_name || `Shop ${row.shop_id}`}
        </div>
        <div className="text-[11px] text-[var(--color-warm-500)] mono-nums mt-0.5">
          ID {row.shop_id}
        </div>
      </td>

      {/* Realm */}
      <td className="px-4 py-3 align-middle">
        {hasRealm ? (
          <span
            title={row.realm_id}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-[var(--radius-xs)] bg-[var(--color-cream-200)] text-[11px] text-[var(--color-warm-700)] mono-nums"
          >
            {realmLabel}
          </span>
        ) : (
          <Badge tone="action" size="xs">
            Not connected
          </Badge>
        )}
      </td>

      {/* Parts Purchases */}
      <td className="px-4 py-3 align-middle min-w-[260px]">
        <PickerCell
          hasRealm={hasRealm}
          value={partsValue}
          onChange={(picked) =>
            onPickAccount(
              row.shop_id,
              'parts_purchases',
              'parts_purchases_account_id',
              'parts_purchases_account_name',
              picked,
            )
          }
          onClear={() =>
            onClearField(
              row.shop_id,
              'parts_purchases_account_id',
              'parts_purchases_account_name',
            )
          }
          fetchOptions={fetchAccounts}
          placeholder="Pick an account…"
          emptyHint="No active accounts returned from QuickBooks."
        />
      </td>

      {/* Sublet */}
      <td className="px-4 py-3 align-middle min-w-[260px]">
        <PickerCell
          hasRealm={hasRealm}
          value={subletValue}
          onChange={(picked) =>
            onPickAccount(
              row.shop_id,
              'sublet',
              'sublet_account_id',
              'sublet_account_name',
              picked,
            )
          }
          onClear={() =>
            onClearField(row.shop_id, 'sublet_account_id', 'sublet_account_name')
          }
          fetchOptions={fetchAccounts}
          placeholder="Pick an account…"
          emptyHint="No active accounts returned from QuickBooks."
        />
      </td>

      {/* Class */}
      <td className="px-4 py-3 align-middle min-w-[220px]">
        <PickerCell
          hasRealm={hasRealm}
          value={classValue}
          onChange={(picked) =>
            onPickAccount(
              row.shop_id,
              'class',
              'qbo_class_id',
              'qbo_class_name',
              picked,
            )
          }
          onClear={() =>
            onClearField(row.shop_id, 'qbo_class_id', 'qbo_class_name')
          }
          fetchOptions={fetchClasses}
          placeholder="No class…"
          emptyHint="This QBO company doesn't use Classes."
        />
      </td>

      {/* Save */}
      <td className="px-4 py-3 align-middle text-right">
        <div className="flex flex-col items-end gap-1">
          <Button
            size="sm"
            variant={row._dirty ? 'primary' : 'ghost'}
            disabled={!row._dirty || row._saving}
            loading={row._saving}
            leftIcon={!row._saving && <Save size={13} />}
            onClick={onSave}
          >
            {row._dirty ? 'Save' : 'Saved'}
          </Button>
          {row._saveError && (
            <span className="text-[11px] text-[var(--color-danger-600)] max-w-[180px] text-right">
              {row._saveError}
            </span>
          )}
          {!row._saveError && (row._savedAt || row.updated_at) && (
            <span className="inline-flex items-center gap-1 text-[11px] text-[var(--color-warm-500)]">
              {row._savedAt && (
                <CheckCircle2 size={11} className="text-[var(--color-recovery-500)]" />
              )}
              Last saved {formatRelative(row._savedAt || row.updated_at)}
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

function PickerCell({ hasRealm, ...pickerProps }) {
  if (!hasRealm) {
    return (
      <div className="text-[11px] text-[var(--color-warm-500)] italic">
        QBO not connected for this shop.
      </div>
    );
  }
  if (!pickerProps.value) {
    return (
      <div>
        <QboPicker {...pickerProps} />
        <div className="mt-1 text-[10px] text-[var(--color-warm-500)]">
          Pick an account from the dropdown to configure.
        </div>
      </div>
    );
  }
  return <QboPicker {...pickerProps} />;
}
